import { lazy, Suspense } from 'react';
import { isNotFound } from '@tanstack/react-router';
import { usePortalCampaignQuery } from '@/lib/data-hooks';
import { Image } from '../../-components/image';
import { useCampaignDisplayContext } from './campaign-display-context';

const GalleryContent = lazy(() => import('./campaign-image-gallery-content'));

export function CampaignImageGallery(props: { campaignId: string }) {
	const displayData = useCampaignDisplayContext();
	if (displayData) {
		const { campaign } = displayData;
		const heroImage = buildHeroLeadingImage(campaign.media?.location, campaign.heroImageAlt);
		const images = [
			...heroImage,
			...(campaign.carouselImages ?? []).map((img, index) => buildGalleryImage(img, index)),
		];
		if (images.length === 0) return null;
		return (
			<Suspense fallback={<GalleryFallback />}>
				<GalleryContent images={images} />
			</Suspense>
		);
	}
	return (
		<Suspense fallback={<GalleryFallback />}>
			<SuspensedCampaignImageGallery campaignId={props.campaignId} />
		</Suspense>
	);
}

const galleryPadding = 'max(var(--gallery-inset), calc((100% - 1366px) / 2 + var(--gallery-inset)))';

function GalleryFallback() {
	return (
		<section className="w-full pb-4 sm:pb-6">
			<div
				className="flex h-[286px] gap-4 overflow-hidden [--gallery-inset:1.25rem] sm:gap-5 sm:[--gallery-inset:2.5rem]"
				style={{ paddingLeft: galleryPadding, paddingRight: galleryPadding }}
			/>
		</section>
	);
}

function SuspensedCampaignImageGallery(props: { campaignId: string }) {
	const campaign = usePortalCampaignQuery(props.campaignId);

	if (isNotFound(campaign)) return null;

	const heroImage = buildHeroLeadingImage(campaign.data.media?.location, campaign.data.heroImageAlt);
	const images = [
		...heroImage,
		...(campaign.data.carouselImages ?? []).map((img, index) => buildGalleryImage(img, index)),
	];
	if (images.length === 0) return null;

	return <GalleryContent images={images} />;
}

// On mobile the hero image is merged into the carousel as its first slide (hidden from lg up,
// where the hero image is already shown next to the campaign title) so we gain vertical space.
function buildHeroLeadingImage(location: string | undefined, alt: string | undefined) {
	if (!location) return [];
	return [
		{
			key: `hero-${location}`,
			alt: alt ?? 'Campaign hero image',
			mobileOnly: true,
			image: (
				<Image
					src={location}
					height={286}
					aspectRatio={413 / 286}
					className="w-full! rounded-2xl object-cover"
					alt={alt ?? 'Campaign hero image'}
				/>
			),
		},
	];
}

function buildGalleryImage(img: { location: string; alt?: string }, index: number) {
	return {
		key: img.location,
		alt: img.alt ?? `Campaign gallery ${index + 1}`,
		image: (
			<Image
				src={img.location}
				height={286}
				aspectRatio={413 / 286}
				className="w-full! rounded-2xl object-cover"
				alt={img.alt ?? `Campaign gallery ${index + 1}`}
			/>
		),
	};
}
